import { cn } from '@/lib/utils';

// Add wiggle animation styles
const wiggleStyles = `
  @keyframes wiggle {
    0%, 54.5% { transform: rotate(0deg); }
    13.6% { transform: rotate(2deg); }
    27.3% { transform: rotate(-1deg); }
    40.9% { transform: rotate(1.5deg); }
    54.5%, 100% { transform: rotate(0deg); }
  }
  .logo-container:hover #antena {
    animation: wiggle 1.1s ease-in-out infinite;
  }
`;

interface LogoProps {
  size?: 'small' | 'medium' | 'large' | 'extra-large';
  className?: string;
  showText?: boolean;
  showSvgText?: boolean;
}

export default function Logo({
  size = 'medium',
  className,
  showText = true,
  showSvgText = true,
}: LogoProps) {
  const sizeClasses = {
    small: 'h-8 w-auto',
    medium: 'h-12 w-auto',
    large: 'h-20 w-auto',
    'extra-large': 'h-40 w-auto',
  };

  const textSizeClasses = {
    small: 'text-base',
    medium: 'text-lg',
    large: 'text-3xl',
    'extra-large': 'text-5xl',
  };

  const gapClasses = {
    small: 'gap-2',
    medium: 'gap-3',
    large: 'gap-4',
    'extra-large': 'gap-6',
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: wiggleStyles }} />
      <div
        className={cn(
          'flex flex-row font-medium select-none items-center logo-container',
          gapClasses[size],
          className,
        )}
      >
        {showSvgText ? (
          <svg
            width="192.24716"
            height="134.71391"
            viewBox="0 0 192.24716 134.71391"
            className={cn('shrink-0', sizeClasses[size])}
            xmlns="http://www.w3.org/2000/svg"
          >
            <g transform="translate(-0.6020396,-9.8397994)">
              <g transform="translate(0,10.128517)">
                <ellipse
                  style={{
                    fill: '#2563eb',
                    fillOpacity: 1,
                    stroke: 'none',
                    strokeWidth: 0.598057,
                    strokeOpacity: 0.921569,
                  }}
                  cx="38.353622"
                  cy="45.600292"
                  rx="19.374029"
                  ry="18.948227"
                />
                <path
                  id="body"
                  style={{
                    fill: '#2563eb',
                    fillOpacity: 1,
                    stroke: 'none',
                    strokeWidth: 0.598057,
                    strokeOpacity: 0.921569,
                  }}
                  d="m 28.0248,66.071359 c 3.167242,1.280554 10.614392,2.273098 15.930331,1.280554 5.315939,-0.992546 7.282717,-0.709175 9.156218,1.771517 5.531869,7.324725 3.927219,17.271991 -8.486252,26.724244 -12.413472,9.452246 -18.359619,10.157816 -19.829713,14.943406 -1.470094,4.78559 0.09082,8.04909 0.921753,9.54762 0.830936,1.49853 -0.801473,2.44502 -1.854791,1.51291 -1.053317,-0.9321 -4.383111,-4.22536 -5.43266,-7.28824 -1.049549,-3.06288 -0.634905,-7.54186 0.662218,-10.89941 1.297125,-3.35756 2.505815,-6.116302 2.530989,-8.858458 0.02517,-2.742155 -0.686668,-4.613365 -1.86102,-6.997431 -1.174353,-2.384066 -2.859051,-5.279552 -2.754311,-8.411812 0.10474,-3.132261 1.705846,-6.938023 3.722041,-9.230654 2.016196,-2.292632 4.70998,-5.13948 7.295197,-4.094246 z"
                />
                <g
                  id="antena"
                  style={{
                    transformOrigin: '50% 100%',
                  }}
                >
                  <path
                    style={{
                      fill: '#2563eb',
                      fillOpacity: 1,
                      stroke: 'none',
                      strokeWidth: 0.598057,
                      strokeOpacity: 0.921569,
                    }}
                    d="m 36.093518,28.46955 4.113058,-10.693948 1.182505,0.154239 0.462718,0.205653 -1.696635,10.334056 z"
                  />
                  <ellipse
                    style={{
                      fill: '#2563eb',
                      fillOpacity: 1,
                      stroke: 'none',
                      strokeWidth: 0.598057,
                      strokeOpacity: 0.921569,
                    }}
                    cx="41.157722"
                    cy="15.89902"
                    rx="3.9331112"
                    ry="3.9845245"
                  />
                </g>
                <ellipse
                  style={{
                    fill: '#ffffff',
                    fillOpacity: 1,
                    stroke: 'none',
                    strokeWidth: 0.598057,
                    strokeOpacity: 0.921569,
                  }}
                  cx="50.044491"
                  cy="45.600292"
                  rx="5.0482674"
                  ry="4.9846344"
                />
                <ellipse
                  style={{
                    fill: '#ffffff',
                    fillOpacity: 1,
                    stroke: 'none',
                    strokeWidth: 0.598057,
                    strokeOpacity: 0.921569,
                  }}
                  cx="37.017609"
                  cy="45.600292"
                  rx="5.0482674"
                  ry="4.9846344"
                />
              </g>
              <g>
                <text
                  style={{
                    fontWeight: 'bold',
                    fontSize: '67.0536px',
                    lineHeight: 1.1,
                    fontFamily: 'System-ui',
                    fill: '#002b65',
                    fillOpacity: 1,
                    stroke: 'none',
                    strokeWidth: 13.8235,
                    strokeOpacity: 0.921569,
                  }}
                  x="73.247658"
                  y="76.243126"
                  transform="scale(0.98777862,1.0123726)"
                >
                  <tspan
                    style={{
                      fontStyle: 'normal',
                      fontVariant: 'normal',
                      fontWeight: 'bold',
                      fontStretch: 'normal',
                      fontFamily: 'Arial Black',
                      fill: '#002b65',
                      fillOpacity: 1,
                      strokeWidth: 13.8235,
                    }}
                    x="73.247658"
                    y="76.243126"
                  >
                    BA
                  </tspan>
                </text>
                <text
                  style={{
                    fontStyle: 'normal',
                    fontVariant: 'normal',
                    fontWeight: 'bold',
                    fontStretch: 'normal',
                    fontSize: '34.2234px',
                    lineHeight: 1.1,
                    fontFamily: 'Arial Black',
                    fill: '#003f93',
                    fillOpacity: 1,
                    stroke: 'none',
                    strokeWidth: 7.05536,
                    strokeOpacity: 0.921569,
                  }}
                  x="82.468651"
                  y="100.41221"
                  transform="scale(0.9190439,1.0880873)"
                >
                  <tspan
                    style={{
                      fill: '#003f93',
                      fillOpacity: 1,
                      strokeWidth: 7.05536,
                    }}
                    x="82.468651"
                    y="100.41221"
                  >
                    Assist
                  </tspan>
                </text>
                <g transform="translate(0.49604778,2.9112636)">
                  <text
                    style={{
                      fontStyle: 'normal',
                      fontVariant: 'normal',
                      fontWeight: 'bold',
                      fontStretch: 'normal',
                      fontSize: '11.5px',
                      lineHeight: 1.1,
                      fontFamily: 'Arial Black',
                      fill: '#003f93',
                      fillOpacity: 1,
                      stroke: 'none',
                      strokeWidth: 2.0466,
                      strokeOpacity: 0.921569,
                    }}
                    x="83.63607"
                    y="110.85023"
                    transform="scale(0.9190439,1.0880873)"
                  >
                    <tspan
                      style={{
                        fill: '#003f93',
                        fillOpacity: 1,
                        strokeWidth: 2.0466,
                      }}
                      x="83.63607"
                      y="110.85023"
                    >
                      by ITS
                    </tspan>
                  </text>
                </g>
              </g>
            </g>
          </svg>
        ) : (
          <svg
            width="54.476017"
            height="147.55434"
            viewBox="0 0 54.476017 147.55434"
            className={cn('shrink-0', sizeClasses[size])}
            xmlns="http://www.w3.org/2000/svg"
          >
            <g transform="translate(-33.050793,-12.725625)">
              <ellipse
                style={{
                  fill: '#2563eb',
                  fillOpacity: 1,
                  stroke: 'none',
                  strokeWidth: 0.799999,
                  strokeOpacity: 0.921569,
                }}
                cx="61.610878"
                cy="57.785896"
                rx="25.915937"
                ry="25.346359"
              />
              <path
                style={{
                  fill: '#2563eb',
                  fillOpacity: 1,
                  stroke: 'none',
                  strokeWidth: 0.799999,
                  strokeOpacity: 0.921569,
                }}
                d="m 47.794386,85.169301 c 4.236705,1.712951 14.198488,3.040641 21.309427,1.71295 7.110939,-1.327691 9.741827,-0.948636 12.247941,2.369695 7.399782,9.79802 5.253299,23.104124 -11.351752,35.748054 -16.605052,12.64393 -24.558998,13.58775 -26.525489,19.98926 -1.966491,6.40151 0.121482,10.76697 1.232995,12.7715 1.111513,2.00453 -1.072101,3.27061 -2.481086,2.02377 -1.408985,-1.24684 -5.863129,-5.65212 -7.267073,-9.74922 -1.403944,-4.0971 -0.84929,-10.08847 0.885826,-14.57975 1.735116,-4.49128 3.351938,-8.18155 3.385611,-11.84963 0.03367,-3.66808 -0.91853,-6.17113 -2.489419,-9.36021 -1.570889,-3.18908 -3.824448,-7.06227 -3.684342,-11.25218 0.140106,-4.189914 2.281848,-9.280744 4.97884,-12.347514 2.696992,-3.066771 6.30037,-6.874897 9.758521,-5.476725 z"
              />
              <path
                style={{
                  fill: '#2563eb',
                  fillOpacity: 1,
                  stroke: 'none',
                  strokeWidth: 0.799999,
                  strokeOpacity: 0.921569,
                }}
                d="m 58.587618,34.870722 5.501889,-14.304908 1.581793,0.20632 0.618963,0.275095 -2.269529,13.823493 z"
              />
              <ellipse
                style={{
                  fill: '#2563eb',
                  fillOpacity: 1,
                  stroke: 'none',
                  strokeWidth: 0.799999,
                  strokeOpacity: 0.921569,
                }}
                cx="65.361824"
                cy="18.055578"
                rx="5.2611804"
                ry="5.3299541"
              />
              <ellipse
                style={{
                  fill: '#ffffff',
                  fillOpacity: 1,
                  stroke: 'none',
                  strokeWidth: 0.799999,
                  strokeOpacity: 0.921569,
                }}
                cx="77.249329"
                cy="57.785896"
                rx="6.7528849"
                ry="6.6677647"
              />
              <ellipse
                style={{
                  fill: '#ffffff',
                  fillOpacity: 1,
                  stroke: 'none',
                  strokeWidth: 0.799999,
                  strokeOpacity: 0.921569,
                }}
                cx="59.823742"
                cy="57.785896"
                rx="6.7528849"
                ry="6.6677647"
              />
            </g>
          </svg>
        )}
        {showText && (
          <span
            className={cn(
              'group-data-[collapsible=icon]:hidden font-semibold',
              textSizeClasses[size],
            )}
          >
            BA Assist
          </span>
        )}
      </div>
    </>
  );
}
